/**
 * /prompt-cost-predictor — PUBLIC lead magnet.
 * ================================================
 * Paste a prompt + system prompt → instant per-model cost comparison
 * across 11 LLMs. Jan-2026 pricing, deterministic math.
 *
 * No signup. Top-of-funnel viral tool. Drop the email at the bottom
 * if they want the detailed report.
 *
 * Brand: Slate-950, emerald accents, soft amber for warnings.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import SEOHead from '../components/SEOHead';
import FAQSection from '../components/FAQSection';
import {
  Calculator, Sparkles, ArrowLeft, Loader2, ArrowRight, Zap,
  TrendingDown, AlertTriangle, Copy, Check, Share2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: d })}`;
const fmtSmall = (n) => n >= 1 ? fmt(n, 2) : `$${n.toFixed(6)}`;

const PromptCostPredictor = () => {
  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful financial operations assistant for cloud + AI cost optimization.\n\nAnswer questions using deterministic math where possible. Always cite the data source. Output in JSON when asked.",
  );
  const [userPrompt, setUserPrompt] = useState(
    "Which customer had the worst margin last month, and what would happen if we dropped them?",
  );
  const [outputKind, setOutputKind] = useState('analysis');
  const [outputLength, setOutputLength] = useState('normal');
  const [monthlyCalls, setMonthlyCalls] = useState(10000);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (userPrompt.trim().length < 4) {
      toast.error('Add a user prompt (at least 4 characters).');
      return;
    }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API_URL}/prompt-cost/estimate`, {
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        expected_output_kind: outputKind,
        expected_output_length: outputLength,
        monthly_calls: Number(monthlyCalls) || 1000,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Estimate failed — please retry.');
    } finally { setRunning(false); }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const onCopyShare = async () => {
    try {
      const cheapest = result?.estimates?.find((e) => e.id === result.cheapest_id);
      const text = `I ran my prompt through cartieai.com/prompt-cost-predictor — switching to ${cheapest?.id} saves ${result?.cheapest_savings_pct}% (${fmt(result?.cheapest_savings_vs_most_expensive_usd)}/mo) at ${monthlyCalls.toLocaleString()} calls/mo. Try yours: ${shareUrl}`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Sharable summary copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="prompt-cost-predictor">
      {/* v34.17 (Feb 2026): page was bg-white but every card inside was
          bg-slate-900/60 → washed-out grey everywhere. Switched the whole
          page to the dark theme used by /token-intelligence-suite + landing.
          Added the same ambient emerald+teal atmosphere for consistency. */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-emerald-500/[0.08] blur-[140px]" />
        <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-teal-500/[0.06] blur-[120px]" />
        <div className="absolute top-14 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />
      </div>

      <SEOHead
        title="Prompt Cost Predictor · CARTIEAI"
        description="Paste any LLM prompt → instant per-model cost across GPT-5.2, Claude Sonnet 4.5, Gemini 3, DeepSeek, Groq. Free, no signup."
      />
      <header className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="pcp-back">
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:-translate-x-0.5 transition-all" />
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-7 h-7 object-contain" />
            <span className="font-display font-black text-base tracking-tight">
              <span className="text-white">CARTIE</span><span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup>
            </span>
          </Link>
          <Link to="/login">
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              Start free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        <section>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
            <Calculator className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
              Free · No signup · 11 models
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            <span className="text-emerald-400">
              Prompt Cost Predictor
            </span>
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl text-base">
            Paste any LLM prompt → instant per-model cost across GPT-5.2, Claude Sonnet 4.5,
            Gemini 3, DeepSeek, Groq. Jan-2026 pricing, deterministic math, no LLM call needed.
          </p>
        </section>

        <Card className="bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">Your prompt</CardTitle>
            <CardDescription className="text-slate-400">
              Paste your system prompt (stays the same every call) and a representative user prompt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-1.5 block">
                System prompt
              </label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="bg-slate-950 border-white/10 text-slate-100 font-mono text-xs min-h-[120px]"
                data-testid="pcp-system-input"
                maxLength={20000}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-1.5 block">
                User prompt (sample)
              </label>
              <Textarea
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                className="bg-slate-950 border-white/10 text-slate-100 font-mono text-xs min-h-[100px]"
                data-testid="pcp-user-input"
                maxLength={40000}
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Expected output type</label>
                <Select value={outputKind} onValueChange={setOutputKind}>
                  <SelectTrigger className="bg-slate-950 border-white/10" data-testid="pcp-output-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short_answer">Short answer</SelectItem>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="analysis">Analysis</SelectItem>
                    <SelectItem value="code">Code</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="long_form">Long form</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Output length</label>
                <Select value={outputLength} onValueChange={setOutputLength}>
                  <SelectTrigger className="bg-slate-950 border-white/10" data-testid="pcp-output-length">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brief">Brief</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="detailed">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Monthly calls</label>
                <Input
                  type="number"
                  value={monthlyCalls}
                  onChange={(e) => setMonthlyCalls(e.target.value)}
                  className="bg-slate-950 border-white/10 text-slate-100"
                  min={1}
                  step={1000}
                  data-testid="pcp-monthly-calls"
                />
              </div>
            </div>
            <Button
              onClick={run}
              disabled={running}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              data-testid="pcp-estimate-btn"
            >
              {running ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Computing</>
                : <><Sparkles className="w-4 h-4 mr-1.5" /> Estimate across 11 models</>}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <>
            <div className="grid sm:grid-cols-4 gap-3" data-testid="pcp-summary">
              {[
                { label: 'Cheapest', val: result.cheapest_id, accent: 'emerald-300' },
                { label: 'Monthly savings', val: fmt(result.cheapest_savings_vs_most_expensive_usd), accent: 'emerald-300' },
                { label: 'Savings %', val: `${result.cheapest_savings_pct}%`, accent: 'cyan-300' },
                { label: 'Input tokens', val: result.total_input_tokens.toLocaleString(), accent: 'slate-300' },
              ].map((s) => (
                <Card key={s.label} className="bg-slate-900/60 border-white/10">
                  <CardContent className="p-4">
                    <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{s.label}</p>
                    <p className={`text-2xl font-bold text-${s.accent} tabular-nums`}>{s.val}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-slate-900/60 border-white/10" data-testid="pcp-results">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base text-slate-900">Per-model breakdown</CardTitle>
                    <CardDescription className="text-slate-400">
                      Cheapest first · sorted by cost-per-call · {monthlyCalls.toLocaleString()} calls/mo
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onCopyShare}
                    className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                    data-testid="pcp-share-btn"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Share2 className="w-3.5 h-3.5 mr-1.5" />}
                    {copied ? 'Copied' : 'Share result'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-white/5">
                        <th className="text-left py-2 pr-3">Model</th>
                        <th className="text-left py-2 pr-3">Provider</th>
                        <th className="text-right py-2 pr-3">Per call</th>
                        <th className="text-right py-2 pr-3">Monthly</th>
                        <th className="text-right py-2 pr-3">Annual</th>
                        <th className="text-left py-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {result.estimates.map((e, idx) => (
                        <tr key={e.id} className={idx === 0 ? 'bg-emerald-500/5' : ''} data-testid={`pcp-row-${e.id}`}>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-2">
                              <code className="text-emerald-300 text-xs">{e.id}</code>
                              {idx === 0 && <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">CHEAPEST</Badge>}
                              {!e.fits_in_context && <Badge variant="outline" className="border-rose-400/40 text-rose-300 text-[11px]">DOESN'T FIT</Badge>}
                            </div>
                          </td>
                          <td className="py-2.5 pr-3 text-slate-400 text-xs">{e.provider}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-slate-100">{fmtSmall(e.cost_per_call_usd)}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-slate-100">{fmt(e.monthly_cost_usd, 2)}</td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-300 font-semibold">{fmt(e.annual_cost_usd, 0)}</td>
                          <td className="py-2.5 text-slate-400 text-xs">{e.vibe}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400 mt-4 italic">{result.disclaimer}</p>
              </CardContent>
            </Card>
          </>
        )}

        {/* CTA */}
        <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Zap className="w-8 h-8 text-emerald-300 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900">Want this running on your actual prompts in production?</h3>
                <p className="text-slate-600 text-sm mt-1.5 mb-4">
                  CARTIEAI tracks every LLM call you make → tells you which features actually pay
                  for themselves, which customers cost more than they pay, and which models you should swap.
                  Free tier. Outcome pricing.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/login">
                    <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="pcp-cta-signup">
                      Start free <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <Link to="/decision-simulator">
                    <Button variant="outline" className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10" data-testid="pcp-cta-decision-sim">
                      Try Decision Simulator
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <FAQSection
          title="Prompt Cost Predictor FAQ"
          items={[
            { id: 'pcp-accuracy', q: 'How accurate is the estimate?', a: 'Within ±5% of each provider\'s official tokenizer for prompts under 10K tokens. Output token projections are heuristic-based — actual usage may vary ±20%. We never under-promise.' },
            { id: 'pcp-prices',   q: 'Where do the prices come from?', a: 'Jan-2026 published list prices from each provider. OpenAI, Anthropic, Google, DeepSeek, Groq. We snapshot quarterly.' },
            { id: 'pcp-cache',    q: 'Do you account for prompt caching?',  a: 'Not yet on this public tool. CARTIE\'s authed Cache-Miss Detective does — it analyzes your actual traffic and tells you which features qualify for 90% input-token savings via Anthropic prompt caching.' },
            { id: 'pcp-train',    q: 'Do you save my prompt?',              a: 'No. We don\'t persist any prompt content. The cost math is stateless.' },
            { id: 'pcp-share',    q: 'Can I share these results?',          a: 'Yes — click Share Result to copy a tweet-ready summary with the savings number.' },
          ]}
          testIdPrefix="pcp-faq"
          variant="card"
        />
      </main>
    </div>
  );
};

export default PromptCostPredictor;
